import { useMutation, useQuery } from "@apollo/client";
import {
  Avatar,
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Text,
  useMantineTheme,
} from "@mantine/core";
import EditIcon from "@mui/icons-material/Edit";

import { showNotification } from "@mantine/notifications";
import { DEL_PRODUCT } from "apollo/mutuations";
import { GET_PRODUCTS } from "apollo/queries";
import ProductAddModal from "components/Product/AdminProductAddModal";
import ProductDetailModal from "components/Product/ProductDetail";
import ProductEditModal from "components/Product/productEditModal";
import ActionButton from "components/controls/ActionButtonNew";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import SupplierFilter from "pages/supplier/supplierFilter";
import { useEffect, useState } from "react";
import { Trash } from "tabler-icons-react";

const SupplierProducts = () => {
  const [size, setSize] = useState("10");
  const [categoryId, setCategoryId] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [hasMounted, setHasMounted] = useState(false);
  const [openedEdit, setOpenedEdit] = useState(false);
  const [openedDetail, setOpenedDetail] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [confirmedSearch, setConfirmedSearch] = useState("");
  const [opened, setOpened] = useState(false);
  const [editId, setEditId] = useState();
  const [deleteID, setDeleteID] = useState(false);
  const [openedAddPrice, setOpenedAddPrice] = useState(false);
  const [openedDelete, setOpenedDelete] = useState(false);
  const theme = useMantineTheme();
  const handleManageProduct = (id) => {
    setEditId(id);
    setOpenedDetail(true);
  };
  const { data, loading, fetchMore, refetch } = useQuery(GET_PRODUCTS, {
    variables: {
      category_id: categoryId,
      first: parseInt(size),
      user_id: parseInt(supplier),
      page: activePage,
      ordered_by: [
        {
          column: "CREATED_AT",
          order: "DESC",
        },
      ],
      search: searchValue,
    },

    onCompleted: (data) => {
      setTotal(data?.products.paginatorInfo.lastPage);
    },

    onError(error) {
      setOpenedDelete(false);
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to get products ",
      });
    },
  });
  useEffect(() => {
    refetch();
  }, []);
  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  const [delProduct] = useMutation(DEL_PRODUCT, {
    update(cache, { data: { deleteProduct } }) {
      cache.updateQuery(
        {
          query: GET_PRODUCTS,
          variables: {
            category_id: categoryId,
            first: parseInt(size),
            page: activePage,
            ordered_by: [
              {
                column: "CREATED_AT",
                order: "DESC",
              },
            ],
            search: searchValue,
          },
        },
        (data) => {
          if (data.products.data.length === 1) {
            setTotal(total - 1);
            setActivePage(activePage - 1);
          } else {
            return {
              products: {
                data: [
                  ...data.products.data.filter(
                    (product) => product.id !== deleteProduct.id,
                  ),
                ],
              },
            };
          }
        },
      );
    },
  });

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const handleChange = (currentPage) => {
    fetchMore({
      variables: {
        first: parseInt(size),
        page: currentPage,
      },
    });
    setActivePage(currentPage);
  };

  if (!hasMounted) {
    return null;
  }
  const headerData = [
    {
      label: "Image",
      key: "avatar",
      sortable: false,
      with: "10%",
      searchable: false,
      render: (rowData) => {
        const defaultImage = rowData?.images?.find((img) => img.is_default);
        return (
          <Avatar.Group spacing="sm">
            <Avatar
              src={
                defaultImage
                  ? defaultImage.original_url
                  : rowData?.images?.[0]?.original_url
              }
              radius="xl"
            />
          </Avatar.Group>
        );
      },
    },
    {
      label: "Name",
      key: "name",
      sortable: false,
      searchable: false,
      render: (rowData) => {
        const MAX_LENGTH = 15;
        const shortName =
          rowData.name.length > MAX_LENGTH
            ? rowData.name.slice(0, MAX_LENGTH) + "..."
            : rowData.name;

        return <span title={rowData.name}>{shortName}</span>;
      },
    },
    {
      label: "Category",
      key: "category",
      width: "20%",
      render: (rowData) => <span>{rowData.category?.name}</span>,
    },
    {
      label: "Supplier",
      key: "supplier",
      width: "28%",
      render: (rowData) => <span>{rowData.supplier?.user?.name}</span>,
    },
    {
      label: "Supplier Phone",
      key: "supplier_phone",
      width: "15%",
      render: (rowData) => <span>{rowData.supplier?.user?.phone}</span>,
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      //   width: "30%",

      render: (rowData) => {
        return (
          <div style={{ width: "150px", display: "flex", gap: "6px" }}>
            <ActionButton
              label="Edit"
              onClick={() => handleEditProduct(rowData)}
            >
              <EditIcon style={{ fontSize: "1rem" }} />
            </ActionButton>
            <ActionButton
              label="Delete"
              color="error"
              onClick={() => handleDelete(`${rowData.id}`)}
            >
              <Trash size={15} />
            </ActionButton>
          </div>
        );
      },
    },
  ];

  const optionsData = {
    actionLabel: "Add Product",
    setAddModal: setOpened,
  };

  const handleDelete = (id) => {
    setOpenedDelete(true);
    setDeleteID(id);
  };

  const handleAddPrice = (id) => {
    setOpenedAddPrice(true);
    setDeleteID(id);
  };

  const deleteProduct = () => {
    delProduct({
      variables: { id: deleteID },
      onCompleted() {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "green",
          title: "Success",
          message: "Product Deleted Successfully",
        });
      },

      onError(error) {
        setOpenedDelete(false);
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to delete product",
        });
      },
    });
  };

  const handleEditProduct = (id) => {
    setOpenedEdit(true);
    setEditId(id);
  };

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };
  console.log(categoryId);
  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Modal
        opened={openedDelete}
        onClose={() => setOpenedDelete(false)}
        title="Warning"
        centered
      >
        <p>Are you sure do you want to delete this product?</p>
        <Group position="right">
          <Button onClick={() => deleteProduct()} color="red">
            Delete
          </Button>
        </Group>
      </Modal>
      <Drawer
        opened={openedDetail}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        title={
          <Text>
            Product Detail,{" "}
            <Text
              style={{
                color: "#FF6A00",
              }}
              span
              fw={600}
            >
              Add Discount, View attributes and values
            </Text>
          </Text>
        }
        padding="xl"
        onClose={() => setOpenedDetail(false)}
        position="bottom"
        size="80%"
      >
        <ProductDetailModal
          total={total}
          setTotal={setTotal}
          activePage={activePage}
          setActivePage={setActivePage}
          setOpenedDetail={setOpenedDetail}
          Id={editId}
        />
      </Drawer>
      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        title="Addding a Product"
        padding="xl"
        size="80%"
        position="bottom"
      >
        <ProductAddModal
          total={total}
          activePage={activePage}
          setActivePage={setActivePage}
          setOpened={setOpened}
          refetchProducts={refetch}
          category_id={categoryId}
        />
      </Drawer>
      <Drawer
        opened={openedEdit}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        onClose={() => setOpenedEdit(false)}
        title="Editing a Product"
        padding="xl"
        size="80%"
        position="bottom"
      >
        <ProductEditModal
          openedEdit={openedEdit}
          setOpened={setOpenedEdit}
          productData={editId}
        />
      </Drawer>
      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            paginatorInfo={data?.products?.paginatorInfo}
            clearInput={clearInput}
            handelSearch={handleManualSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            optionsData={optionsData}
            loading={loading}
            filterData={({ onCardClick }) => (
              <SupplierFilter supplier={supplier} onCardClick={setSupplier} />
            )}
            data={data?.products?.data || []}
            size={size}
            handlePageSizeChange={handlePageSizeChange}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default SupplierProducts;
